import { useQuery } from "@tanstack/react-query";
import axios, { isAxiosError } from "axios";
import { authRepository, type User } from "../repositories/auth.repository";
import { isSessionRejected, refreshSessionOnce } from "../auth/sessionRefresh";
import { shouldAttemptSessionRefresh } from "../auth/sessionRestore";
import { LOCALSTORAGE_TERREIRO_CURRENT } from "./useCachedTerreiro";

export const sessionQueryKey = ["auth", "session"] as const;

function isDevEnv(): boolean {
  try {
    return Boolean(import.meta.env?.DEV);
  } catch {
    return false;
  }
}

/**
 * Get the current terreiro ID from localStorage.
 * This is used to fetch the correct medium/roles for the terreiro context.
 */
const getCurrentTerreiroId = (): string | undefined => {
  try {
    const raw = localStorage.getItem(LOCALSTORAGE_TERREIRO_CURRENT);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (parsed === null) return undefined; // Personal profile mode
    return parsed?.id || undefined;
  } catch {
    return undefined;
  }
};

export async function fetchSession(
  signal: AbortSignal | undefined,
  terreiroId: string | undefined,
): Promise<User | null> {
  try {
    const user = await authRepository.getMe(signal, terreiroId);
    return user ?? null;
  } catch (err: unknown) {
    if (axios.isCancel(err)) {
      throw err;
    }

    const status = isAxiosError(err) ? err.response?.status : undefined;
    if (status !== 401) {
      throw err;
    }

    if (!shouldAttemptSessionRefresh()) {
      return null;
    }

    if (isDevEnv()) {
      console.warn("[session] /profile/me returned 401, attempting refresh", {
        terreiroId: terreiroId ?? null,
      });
    }

    try {
      await refreshSessionOnce(() => authRepository.refresh());
    } catch (refreshErr: unknown) {
      if (isDevEnv()) {
        console.warn("[session] refresh failed after /profile/me 401", {
          terreiroId: terreiroId ?? null,
          error: isAxiosError(refreshErr)
            ? refreshErr.response?.data
            : String(refreshErr),
        });
      }
      if (isSessionRejected(refreshErr)) {
        return null;
      }
      // The API was unreachable, not the session invalid: let the query retry
      // or show the error state instead of signing the user out.
      throw refreshErr;
    }

    const user = await authRepository.getMe(signal, terreiroId);
    return user ?? null;
  }
}

export class SessionCheckTimeoutError extends Error {
  constructor() {
    super("session_check_timeout");
    this.name = "SessionCheckTimeoutError";
  }
}

export const SESSION_CHECK_TIMEOUT_MS = 12_000;

/**
 * Bounds one session check so "Verificando sua sessão" can't hang on a slow
 * API (getMe + refresh + getMe could otherwise take 40 s).
 */
export function fetchSessionWithDeadline(
  signal: AbortSignal | undefined,
  terreiroId: string | undefined,
  timeoutMs: number = SESSION_CHECK_TIMEOUT_MS,
): Promise<User | null> {
  const controller = new AbortController();
  const abortFromCaller = () => controller.abort();
  signal?.addEventListener("abort", abortFromCaller);

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      controller.abort();
      reject(new SessionCheckTimeoutError());
    }, timeoutMs);
  });

  return Promise.race([fetchSession(controller.signal, terreiroId), deadline]).finally(
    () => {
      clearTimeout(timeoutId);
      signal?.removeEventListener("abort", abortFromCaller);
    },
  );
}

/** Retry once for transient failures (cold start, timeout); never for cancels. */
export const shouldRetrySessionCheck = (
  failureCount: number,
  error: unknown,
): boolean => {
  if (failureCount >= 1) return false;
  if (axios.isCancel(error)) return false;
  return !isSessionRejected(error);
};

export const useSessionQuery = () => {
  // Read terreiroId reactively so the query key updates when profile switches
  const terreiroId = getCurrentTerreiroId();

  return useQuery<User | null>({
    queryKey: [...sessionQueryKey, terreiroId ?? "personal"],
    queryFn: ({ signal }) => {
      // Read FRESH terreiroId inside queryFn to avoid stale closures.
      // The query might be triggered by invalidation after a profile switch;
      // the closure `terreiroId` could still hold the old value.
      const freshTerreiroId = getCurrentTerreiroId();

      if (isDevEnv()) {
        console.info("[session] fetching /profile/me", {
          terreiroId: freshTerreiroId ?? null,
        });
      }

      return fetchSessionWithDeadline(signal, freshTerreiroId).catch((err: unknown) => {
        if (isDevEnv()) {
          const status = isAxiosError(err) ? err.response?.status : undefined;
          console.error("[session] /profile/me unexpected error", {
            status,
            error: isAxiosError(err) ? err.response?.data : String(err),
          });
        }

        throw err;
      });
    },
    staleTime: 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: shouldRetrySessionCheck,
    retryDelay: 1_000,
  });
};
